import React, { useMemo, useState } from 'react';
import { STATUS } from './StudentStatusBadges';

// 名簿の取り込み前に「誰が入るか」を一覧で見せる【2026-09-17】
//   サーバーの確認（dryRun）が返す byClass[].students を表にする。
//   1行 = ファイルの1行。状態ごとに絞り込める。
//
//   status: enroll（新しく履修者になる）/ already（もう履修者）/ skip（取り込まない）/ error
//   accountCreated … 仮アカウントを新しく作る（アカウント未作成）
//   noAccount      … アカウント未作成（本人がまだアプリに登録していない）
//   renamedFrom    … 表示名が名簿の氏名に変わる（元の表示名）
//   nameKept       … 手動で直した氏名なので据え置く

const ROW = {
  enroll:  { label: '新しく履修', color: '#166534', bg: '#dcfce7' },
  already: { label: '登録済み',   color: '#475569', bg: '#f1f5f9' },
  skip:    { label: '取り込まない', color: '#6b7280', bg: '#f3f4f6' },
  error:   { label: 'エラー',     color: '#b91c1c', bg: '#fee2e2' },
};

const FILTERS = [
  { key: 'all',       label: 'すべて',              test: () => true },
  { key: 'enroll',    label: '新しく履修',          test: s => s.status === 'enroll' },
  { key: 'already',   label: '登録済み',            test: s => s.status === 'already' },
  { key: 'noAccount', label: STATUS.noAccount.label, test: s => s.noAccount && s.status !== 'error' && s.status !== 'skip' },
  { key: 'renamed',   label: '氏名が変わる',        test: s => !!s.renamedFrom },
  { key: 'skip',      label: '取り込まない',        test: s => s.status === 'skip' },
  { key: 'error',     label: 'エラー',              test: s => s.status === 'error' },
];

function Tag({ color, bg, children, title }) {
  return (
    <span title={title}
          style={{ fontSize: '0.7rem', fontWeight: 600, color, background: bg || 'transparent',
                   border: bg ? 'none' : `1px solid ${color}`, borderRadius: 4,
                   padding: '0.05rem 0.35rem', marginRight: '0.25rem', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

export default function RosterPreviewStudents({ students = [], maxHeight = 360 }) {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const counts = useMemo(() => {
    const c = {};
    FILTERS.forEach(f => { c[f.key] = students.filter(f.test).length; });
    return c;
  }, [students]);

  const shown = useMemo(() => {
    const f = FILTERS.find(x => x.key === filter) || FILTERS[0];
    const needle = q.trim().toLowerCase();
    return students.filter(s => f.test(s) && (!needle
      || String(s.studentId || '').toLowerCase().includes(needle)
      || String(s.name || '').toLowerCase().includes(needle)));
  }, [students, filter, q]);

  if (students.length === 0) {
    return <div style={{ fontSize: '0.82rem', color: '#888' }}>学生の行はありません</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.4rem' }}>
        {FILTERS.filter(f => f.key === 'all' || counts[f.key] > 0).map(f => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                  style={{
                    fontSize: '0.75rem', padding: '0.1rem 0.5rem', borderRadius: 999, cursor: 'pointer',
                    border: '1px solid #cbd5e1',
                    background: filter === f.key ? '#334155' : '#fff',
                    color: filter === f.key ? '#fff' : '#334155',
                  }}>
            {f.label} {counts[f.key]}
          </button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="学籍番号・氏名で検索"
               style={{ marginLeft: 'auto', fontSize: '0.8rem', padding: '0.15rem 0.45rem', minWidth: 150 }} />
      </div>

      <div style={{ maxHeight, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
        <table className="data-table" style={{ margin: 0, fontSize: '0.82rem' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', top: 0, background: '#f8fafc', width: 44, textAlign: 'right' }}>行</th>
              <th style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>学籍番号</th>
              <th style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>氏名（ファイル）</th>
              <th style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>取り込むと</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(s => {
              const r = ROW[s.status] || ROW.already;
              return (
                <tr key={s.row}>
                  <td style={{ textAlign: 'right', color: '#9ca3af' }}>{s.row}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{s.studentId || '—'}</td>
                  <td>{s.name || <span style={{ color: '#9ca3af' }}>（氏名なし）</span>}</td>
                  <td>
                    <Tag color={r.color} bg={r.bg}>{r.label}</Tag>
                    {s.accountCreated && (
                      <Tag color={STATUS.noAccount.color}
                           title="この学生はまだアプリのアカウントが無いので、ログインできない仮アカウントを作ります。本人が登録すると引き継がれます">
                        仮アカウントを作成
                      </Tag>
                    )}
                    {!s.accountCreated && s.noAccount && s.status !== 'error' && s.status !== 'skip' && (
                      <Tag color={STATUS.noAccount.color} title={STATUS.noAccount.title}>{STATUS.noAccount.label}</Tag>
                    )}
                    {s.renamedFrom && (
                      <Tag color="#1d4ed8" title="表示名を名簿の氏名に合わせます。元の入力は記録として残ります">
                        氏名：{s.renamedFrom} → 名簿の表記
                      </Tag>
                    )}
                    {s.nameKept && (
                      <Tag color="#0f766e" title="手動で直した氏名なので、名簿の氏名では上書きしません">
                        氏名は据え置き（{s.nameKept}）
                      </Tag>
                    )}
                    {s.note && <div style={{ fontSize: '0.75rem', color: r.color }}>{s.note}</div>}
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888' }}>該当する行はありません</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
